import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, runTransaction } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "./PropertiesClients.css";
import PropertyManagersEditor from "./PropertyManagersEditor";
import {
  buildCompanyManagerFields,
  cleanManagerList,
  companyManagerSlotCount,
  defaultManagerFor,
  findCompanyManager,
  primaryManagerFields,
  readPropertyManagers,
} from "./managerUtils";

const PROPERTY_TYPES = [
  "Owner",
  "Single-Family",
  "Condo",
  "Townhome",
  "Commercial",
  "Other",
].sort((a, b) => a.localeCompare(b));

const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS",
  "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY",
  "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV",
  "WI", "WY",
];

/**
 * Pest-control pricing that lives on the property document but is edited on
 * other pages (Scheduling, Contracts, Proposals PPD) and read by
 * resolveServicePricing / invoices. This page never edits these, so they are
 * left out of the update payload: saving a property here can never overwrite
 * pricing someone changed on another page after this list was loaded.
 */
const PRICING_FIELDS = ["servicePricingOverrides", "serviceTypeTotals", "serviceTotals"];

const emptyProperty = () => ({
  propertyName: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  clientPhone: "",
  clientEmail: "",
  accountingEmail: "",
  bodPresident: "",
  presidentEmail: "",
  presidentPhone: "",
  management: "",
  managementAddress: "",
  managementPhone: "",
  managerName: "",
  managementEmail: "",
  notes: "",
  fileUpload: "",
  county: "",
  propertyType: "",
  managementCityStateZip: "",
  managerEmail: "",
  managers: [],
});

/* ------------------------------------------------------------------------
   Popups render through createPortal with self-contained inline styles so
   the app-wide .modal / .modal-overlay / .popup rules can't move them.
   ------------------------------------------------------------------------ */
const overlayStyle = (z) => ({
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.55)",
  zIndex: z,
});

const boxStyle = (z, width = 720) => ({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  zIndex: z,
  width: `min(${width}px, calc(100vw - 32px))`,
  maxHeight: "86vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: "20px 22px",
  border: "1px solid #dbe2ea",
  borderRadius: 12,
  background: "#ffffff",
  boxShadow: "0 18px 48px rgba(15,23,42,.24)",
  boxSizing: "border-box",
  textAlign: "left",
});

const scrollStyle = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
  WebkitOverflowScrolling: "touch",
  paddingRight: 4,
};

const Popup = ({ level = 1, width, children }) => {
  const base = level === 2 ? 1999998 : 999998;
  return createPortal(
    <>
      <div style={overlayStyle(base)} />
      <div className="pcl-scope pcl-popbox" style={boxStyle(base + 1, width)}>
        {children}
      </div>
    </>,
    document.body
  );
};

const formatMoney = (value) => {
  if (value === "" || value == null) return "";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value);
};

/** Read-only summary of per-service pricing set elsewhere in the app. */
const readPricingRows = (property = {}) => {
  const overrides = property.servicePricingOverrides || {};
  const legacy = property.serviceTypeTotals || property.serviceTotals || {};
  const codes = Array.from(new Set([...Object.keys(overrides), ...Object.keys(legacy)])).sort();
  return codes
    .map((code) => {
      const o = overrides[code] || {};
      const amount = o.amount ?? o.total ?? legacy[code] ?? "";
      return {
        code,
        amount,
        initialAmount: o.initialAmount ?? "",
        feeSchedule: o.feeSchedule || "",
      };
    })
    .filter((r) => r.amount !== "" || r.initialAmount !== "" || r.feeSchedule);
};

const PropertiesClients = () => {
  const [properties, setProperties] = useState([]);
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [newProperty, setNewProperty] = useState(emptyProperty());
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProperty, setEditedProperty] = useState({});
  const [showDetails, setShowDetails] = useState(false);
  const [showAddPropertyForm, setShowAddPropertyForm] = useState(false);
  const [showAddNotePopup, setShowAddNotePopup] = useState(false); // Controls the Add Note popup
  const [newNote, setNewNote] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]); // Update to handle multiple files
  const [searchTerm, setSearchTerm] = useState(''); // Search term
  const [sortOption, setSortOption] = useState(''); // Sort option
  const [filteredProperties, setFilteredProperties] = useState([]); // Filtered properties for search and sort
  const [showSuccessPopup, setShowSuccessPopup] = useState(false); // Controls the success popup

  useEffect(() => {
    const fetchProperties = async () => {
      const propertiesRef = collection(db, "properties");
      const querySnapshot = await getDocs(propertiesRef);

      const loaded = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setProperties(loaded);

      // Backfill missing propertyId values
      await backfillPropertyIds(loaded);
    };

    const fetchManagementCompanies = async () => {
      const managementRef = collection(db, "managementCompanies");
      const querySnapshot = await getDocs(managementRef);
      // Keep the doc id: saving a brand-new manager from this page writes it
      // back onto the management company document.
      setManagementCompanies(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    };

    fetchProperties();
    fetchManagementCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterAndSortProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, searchTerm, sortOption]);

  const handleAddNote = async () => {
    if (!newNote && uploadedFiles.length === 0) {
      alert("Please enter a note or upload a file before saving.");
      return;
    }

    try {
      const propertyDoc = doc(db, "properties", selectedProperty.id);
      await updateDoc(propertyDoc, {
        notes: newNote || editedProperty.notes,
        fileUpload: uploadedFiles.length > 0 ? uploadedFiles : editedProperty.fileUpload || [],
      });

      setProperties((prev) =>
        prev.map((p) => (p.id === selectedProperty.id ? { ...p, notes: newNote || p.notes, fileUpload: uploadedFiles.length > 0 ? uploadedFiles : p.fileUpload } : p))
      );

      alert("Notes successfully added!");
      setShowAddNotePopup(false); // Close the Add Note popup
      setNewNote(""); // Reset new note
      setUploadedFiles([]); // Reset uploaded files
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Simulating file upload (You can integrate Firebase Storage here)
    const fileURLs = files.map(file => URL.createObjectURL(file));

    setUploadedFiles(fileURLs);
    setEditedProperty((prev) => ({
      ...prev,
      fileUpload: fileURLs,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProperty((prev) => ({ ...prev, [name]: value }));
  };

  const companyByName = (name) =>
    managementCompanies.find((m) => m.management === name) || null;

  /**
   * Any row marked "+ New manager" is appended to the management company's
   * numbered slots (managerName{n} / managerTitle{n} / ...), so it shows up on
   * the Management page and can be picked for other properties.
   * Names that already exist on the company (case-insensitive) are not duplicated.
   */
  const saveNewManagersToCompany = async (companyName, rows) => {
    const company = companyByName(companyName);
    if (!company || !company.id) return;

    const seen = new Set();
    const toAdd = cleanManagerList((rows || []).filter((r) => r.isNew)).filter((m) => {
      const key = m.name.toLowerCase();
      if (seen.has(key) || findCompanyManager(company, m.name)) return false;
      seen.add(key);
      return true;
    });
    if (toAdd.length === 0) return;

    const fields = buildCompanyManagerFields(toAdd, companyManagerSlotCount(company) + 1);
    await updateDoc(doc(db, "managementCompanies", company.id), fields);
    setManagementCompanies((prev) =>
      prev.map((m) => (m.id === company.id ? { ...m, ...fields } : m))
    );
  };

  /** managers array + the flat primary fields (managerName/Email/Phone/Title) other pages and Cloud Functions read. */
  const managerPayload = (rows) => {
    const managers = cleanManagerList(rows);
    return { managers, ...primaryManagerFields(managers) };
  };

  const PROPERTY_COUNTER_REF = doc(db, "counters", "property_id");

  const getNextPropertyNumber = async () => {
    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(PROPERTY_COUNTER_REF);
      let last = 20100;

      if (!snap.exists()) {
        tx.set(PROPERTY_COUNTER_REF, { lastNumber: 20100 });
      } else {
        const raw = Number(snap.data()?.lastNumber);
        last = Number.isFinite(raw) ? raw : 20100;
      }

      const next = last + 1;
      tx.update(PROPERTY_COUNTER_REF, { lastNumber: next });
      return next;
    });
  };

  const backfillPropertyIds = async (existingProperties) => {
    for (const prop of existingProperties) {
      if (prop.propertyId) continue;

      const nextNumber = await getNextPropertyNumber();
      const zip = prop.zip || "00000";
      const propertyId = `${nextNumber}-${zip}`;

      const ref = doc(db, "properties", prop.id);
      await updateDoc(ref, { propertyId });
    }
  };

  const handleInputEdit = (e) => {
    const { name, value } = e.target;
    setEditedProperty((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setNewProperty(emptyProperty());
    setShowAddPropertyForm(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedProperty({
      ...selectedProperty,
      managers: readPropertyManagers(selectedProperty, companyByName(selectedProperty.management)),
    }); // Reset form fields to original data
  };

  const cityStateZipOf = (m) =>
    `${m.managementCity || ""}, ${m.managementState || ""} ${m.managementZip || ""}`.trim();

  const handleManagementChange = (e) => {
    const selectedManagement = e.target.value;
    const managementData = managementCompanies.find(m => m.management === selectedManagement);

    if (managementData) {
      // Auto-picks one manager, with email/phone/title filled in.
      const first = defaultManagerFor(managementData);

      setNewProperty((prev) => ({
        ...prev,
        management: selectedManagement,
        managementAddress: managementData.managementAddress || "",
        managementPhone: managementData.managementPhone || "",
        managementEmail: managementData.managementEmail || "",
        managementCityStateZip: cityStateZipOf(managementData),
        managers: first ? [{ ...first, isNew: false }] : []
      }));
    } else {
      setNewProperty((prev) => ({
        ...prev,
        management: "",
        managementAddress: "",
        managementPhone: "",
        managementEmail: "",
        managementCityStateZip: "",
        managers: [],
      }));
    }
  };

  const handleManagementChangeEdit = (e) => {
    const selectedManagement = e.target.value;
    const managementData = managementCompanies.find(m => m.management === selectedManagement);

    if (managementData) {
      const first = defaultManagerFor(managementData);

      setEditedProperty((prev) => ({
        ...prev,
        management: selectedManagement,
        managementAddress: managementData.managementAddress || "",
        managementPhone: managementData.managementPhone || "",
        managementEmail: managementData.managementEmail || "",
        managementCityStateZip: cityStateZipOf(managementData),
        managers: first ? [{ ...first, isNew: false }] : []
      }));
    } else {
      // "No Management / Remove Management"
      setEditedProperty((prev) => ({
        ...prev,
        management: "",
        managementAddress: "",
        managementPhone: "",
        managementEmail: "",
        managementCityStateZip: "",
        managers: [],
      }));
    }
  };

  const handleAddProperty = async () => {
    try {
      const nextNumber = await getNextPropertyNumber();
      const zip = newProperty.zip || "00000";

      const propertyId = `${nextNumber}-${zip}`;

      await saveNewManagersToCompany(newProperty.management, newProperty.managers);

      const propertyData = {
        ...newProperty,
        ...managerPayload(newProperty.managers),
        propertyId,
      };

      const propertiesRef = collection(db, "properties");
      const docRef = await addDoc(propertiesRef, propertyData);

      setProperties([...properties, { id: docRef.id, ...propertyData }]);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error adding property:", error);
    }
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    setShowAddPropertyForm(false); // Close the form
    setNewProperty(emptyProperty()); // Clear form data
  };

  const handleDetailsClick = (property) => {
    setSelectedProperty(property);
    setEditedProperty({
      ...property,
      managers: readPropertyManagers(property, companyByName(property.management)),
    });
    setShowDetails(true);
    setIsEditing(false);
    setShowAddPropertyForm(false); // Close Add form when viewing details
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setShowAddPropertyForm(false); // Ensure Add form is not displayed while editing
  };

  const handleUpdate = async () => {
    try {
      await saveNewManagersToCompany(editedProperty.management, editedProperty.managers);

      const { propertyId, id, ...rest } = editedProperty;
      // Pricing maps are owned by other pages; never write a stale copy back.
      PRICING_FIELDS.forEach((f) => delete rest[f]);
      const saved = { ...rest, ...managerPayload(editedProperty.managers), propertyId };

      const propertyDoc = doc(db, "properties", selectedProperty.id);
      await updateDoc(propertyDoc, saved);

      const pricingKept = {};
      PRICING_FIELDS.forEach((f) => {
        if (selectedProperty[f] !== undefined) pricingKept[f] = selectedProperty[f];
      });
      const updated = { id: selectedProperty.id, ...pricingKept, ...saved };
      setProperties((prev) =>
        prev.map((p) => (p.id === selectedProperty.id ? updated : p))
      );
      // The details view reads selectedProperty; keep it in sync with the save.
      setSelectedProperty(updated);
      setEditedProperty({ ...updated });

      setIsEditing(false);
    } catch (error) {
      console.error("Error updating property:", error);
    }
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setShowAddNotePopup(false);
    setIsEditing(false);
    setSelectedProperty(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      await deleteDoc(doc(db, "properties", id));
      setProperties(properties.filter((p) => p.id !== id));
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const filterAndSortProperties = () => {
    // Every field is coerced to a string first, so a property saved without a
    // name or management company can't throw and blank the whole list.
    const term = String(searchTerm || "").trim().toLowerCase();

    const searchableText = (property) =>
      [
        property.propertyName,
        property.management,
        property.propertyId,
        property.managerName,
        property.managerEmail,
        property.managerPhone,
        ...readPropertyManagers(property).flatMap((m) => [m.name, m.title, m.email, m.phone]),
        property.address,
        property.city,
        property.state,
        property.zip,
        property.clientEmail,
        property.clientPhone,
        property.accountingEmail,
        property.propertyType,
      ]
        .map((value) => String(value ?? ""))
        .join(" ")
        .toLowerCase();

    let filtered = term
      ? properties.filter((property) => searchableText(property).includes(term))
      : [...properties];

    const byField = (field) => (a, b) =>
      String(a?.[field] ?? "").localeCompare(String(b?.[field] ?? ""), undefined, {
        sensitivity: "base",
      });

    if (sortOption === 'propertyNameAsc') {
      filtered.sort(byField("propertyName"));
    } else if (sortOption === 'propertyNameDesc') {
      filtered.sort((a, b) => byField("propertyName")(b, a));
    } else if (sortOption === 'managementAsc') {
      filtered.sort(byField("management"));
    } else if (sortOption === 'managementDesc') {
      filtered.sort((a, b) => byField("management")(b, a));
    } else if (sortOption === 'managerAsc') {
      filtered.sort(byField("managerName"));
    } else if (sortOption === 'managerDesc') {
      filtered.sort((a, b) => byField("managerName")(b, a));
    }

    setFilteredProperties(filtered);
  };

  const sortedCompanies = [...managementCompanies]
    .filter((m) => m.management)
    .sort((a, b) =>
      a.management.localeCompare(b.management, undefined, { sensitivity: "base" })
    );

  const pricingRows = selectedProperty ? readPricingRows(selectedProperty) : [];

  return (
    <div className="properties-clients-page pcl-scope">
      <header className="pcl-header">
        <div>
          <h1 className="pcl-title">Properties &amp; Clients</h1>
          <p className="pcl-subtitle">
            Every property, its management company, and the manager assigned to it.
          </p>
        </div>
        <button className="blue-button" onClick={() => setShowAddPropertyForm(true)}>
          Add New Property
        </button>
      </header>

      {showAddPropertyForm && !isEditing && (
        <Popup>
          <h3>Add Property</h3>
          <div className="pcl-scroll" style={scrollStyle}>
            <input name="propertyName" placeholder="Property Name" value={newProperty.propertyName} onChange={handleInputChange} />
            <input name="address" placeholder="Address" value={newProperty.address} onChange={handleInputChange} />
            <input name="city" placeholder="City" value={newProperty.city} onChange={handleInputChange} />
            <select name="state" value={newProperty.state} onChange={handleInputChange}>
              <option value="">Select State</option>
              {STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <input name="zip" placeholder="Zip" value={newProperty.zip} onChange={handleInputChange} />
            <input name="clientPhone" placeholder="Client Phone (Optional)" value={newProperty.clientPhone} onChange={handleInputChange} />
            <input name="clientEmail" placeholder="Client Email (Optional)" value={newProperty.clientEmail} onChange={handleInputChange} />
            <input name="accountingEmail" placeholder="Accounting Email (Optional)" value={newProperty.accountingEmail} onChange={handleInputChange} />
            <select name="propertyType" value={newProperty.propertyType} onChange={handleInputChange}>
              <option value="">Select Property Type</option>
              {PROPERTY_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <textarea name="notes" placeholder="Notes" value={newProperty.notes} onChange={handleInputChange}></textarea>
            <select name="management" value={newProperty.management} onChange={handleManagementChange}>
              <option value="">Select Management</option>
              {sortedCompanies.map(m => (
                <option key={m.id || m.management} value={m.management}>{m.management}</option>
              ))}
            </select>

            <input name="managementAddress" value={newProperty.managementAddress} placeholder="Management Address" readOnly />
            <input name="managementPhone" value={newProperty.managementPhone} placeholder="Management Phone" readOnly />
            <input name="managementEmail" value={newProperty.managementEmail} placeholder="Management Email" readOnly />
            <input name="managementCityStateZip" value={newProperty.managementCityStateZip} placeholder="City, State, Zip" readOnly />
            <PropertyManagersEditor
              company={companyByName(newProperty.management)}
              managers={newProperty.managers}
              onChange={(managers) => setNewProperty((prev) => ({ ...prev, managers }))}
            />
          </div>
          <div className="button-group">
            <button className="azul-button" onClick={handleAddProperty}>Save</button>
            <button className="red-button" onClick={handleCancel}>Cancel</button>
          </div>
        </Popup>
      )}

      {showSuccessPopup && (
        <Popup level={2} width={420}>
          <h3>New Property Successfully Added!</h3>
          <div className="button-group">
            <button className="azul-button" onClick={handleCloseSuccessPopup}>OK</button>
          </div>
        </Popup>
      )}

      <div className="search-sort-container">
        <label className="pcl-field pcl-field-grow">
          <span className="pcl-field-label">Search</span>
          <input
            type="text"
            placeholder="Property, management, manager, address, property ID..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </label>

        <label className="pcl-field">
          <span className="pcl-field-label">Sort by</span>
          <select value={sortOption} onChange={handleSortChange}>
            <option value="">Default order</option>
            <option value="propertyNameAsc">Property Name (A-Z)</option>
            <option value="propertyNameDesc">Property Name (Z-A)</option>
            <option value="managementAsc">Management (A-Z)</option>
            <option value="managementDesc">Management (Z-A)</option>
            <option value="managerAsc">Manager (A-Z)</option>
            <option value="managerDesc">Manager (Z-A)</option>
          </select>
        </label>

        <div className="pcl-count">
          {filteredProperties.length}
          {filteredProperties.length === 1 ? " property" : " properties"}
          {searchTerm.trim() ? ` matching "${searchTerm.trim()}"` : ""}
        </div>
      </div>

      <div className="pcl-table-wrap">
        <table className="pcl-table">
          <thead>
            <tr>
              <th>Property Name</th>
              <th>Address</th>
              <th>City</th>
              <th>State</th>
              <th>Zip</th>
              <th>Management</th>
              <th>Manager</th>
              <th>Accounting Email</th>
              <th>Property Type</th>
              <th className="pcl-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProperties.map((property) => (
              <tr key={property.id}>
                <td>
                  <span className="pcl-strong">{property.propertyName || "Unnamed property"}</span>
                  {property.propertyId && (
                    <span className="pcl-sub">ID {property.propertyId}</span>
                  )}
                </td>
                <td>{property.address || <span className="pcl-muted">&mdash;</span>}</td>
                <td>{property.city || <span className="pcl-muted">&mdash;</span>}</td>
                <td>{property.state || <span className="pcl-muted">&mdash;</span>}</td>
                <td>{property.zip || <span className="pcl-muted">&mdash;</span>}</td>
                <td>{property.management || <span className="pcl-muted">&mdash;</span>}</td>
                <td>
                  {(() => {
                    const list = readPropertyManagers(property, companyByName(property.management));
                    if (list.length === 0) return <span className="pcl-muted">No manager assigned</span>;
                    return list.map((m, i) => (
                      <div className="pcl-mgr-cell" key={`${m.name}-${i}`}>
                        <span className="pcl-strong">{m.name}</span>
                        {m.title && <span className="pcl-chip pcl-chip-sm">{m.title}</span>}
                        {i === 0 && m.email && <span className="pcl-sub">{m.email}</span>}
                      </div>
                    ));
                  })()}
                </td>
                <td>{property.accountingEmail || <span className="pcl-muted">&mdash;</span>}</td>
                <td>
                  {property.propertyType ? (
                    <span className="pcl-chip">{property.propertyType}</span>
                  ) : (
                    <span className="pcl-muted">&mdash;</span>
                  )}
                </td>
                <td className="pcl-col-actions">
                  <div className="button-group">
                    <button className="azul-button" onClick={() => handleDetailsClick(property)}>Details</button>
                    <button className="red-button" onClick={() => handleDelete(property.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}

            {filteredProperties.length === 0 && (
              <tr>
                <td className="pcl-empty" colSpan={10}>
                  {searchTerm.trim()
                    ? `No properties match "${searchTerm.trim()}".`
                    : "No properties yet. Use Add New Property to create one."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDetails && selectedProperty && (
        <Popup>
          <h3>{isEditing ? "Edit Property" : "Property Details"}</h3>
          {isEditing ? (
            <>
              <div className="pcl-scroll" style={scrollStyle}>
                <label>Property ID:</label>
                <input value={editedProperty.propertyId || ""} readOnly />

                <label>Property Name:</label>
                <input name="propertyName" value={editedProperty.propertyName || ""} onChange={handleInputEdit} />

                <label>Address:</label>
                <input name="address" value={editedProperty.address || ""} onChange={handleInputEdit} />

                <label>City:</label>
                <input name="city" value={editedProperty.city || ""} onChange={handleInputEdit} />

                <label>State:</label>
                <select name="state" value={editedProperty.state || ""} onChange={handleInputEdit}>
                  <option value="">Select State</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>

                <label>Zip:</label>
                <input name="zip" value={editedProperty.zip || ""} onChange={handleInputEdit} />

                <label>Client Phone:</label>
                <input name="clientPhone" value={editedProperty.clientPhone || ""} onChange={handleInputEdit} />

                <label>Client Email:</label>
                <input name="clientEmail" value={editedProperty.clientEmail || ""} onChange={handleInputEdit} />

                <label>Accounting Email:</label>
                <input name="accountingEmail" value={editedProperty.accountingEmail || ""} onChange={handleInputEdit} />

                <label>Property Type:</label>
                <select name="propertyType" value={editedProperty.propertyType || ""} onChange={handleInputEdit}>
                  <option value="">Select Property Type</option>
                  {PROPERTY_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>

                <label>Management:</label>
                <select name="management" value={editedProperty.management || ""} onChange={handleManagementChangeEdit}>
                  <option value="">No Management / Remove Management</option>
                  {sortedCompanies.map(m => (
                    <option key={m.id || m.management} value={m.management}>{m.management}</option>
                  ))}
                </select>

                <label>Management Address:</label>
                <input name="managementAddress" value={editedProperty.managementAddress || ""} readOnly />

                <label>Management Phone:</label>
                <input name="managementPhone" value={editedProperty.managementPhone || ""} readOnly />

                <label>Management Email:</label>
                <input name="managementEmail" value={editedProperty.managementEmail || ""} readOnly />

                <label>Management City, State, Zip:</label>
                <input name="managementCityStateZip" value={editedProperty.managementCityStateZip || ""} readOnly />

                <PropertyManagersEditor
                  company={companyByName(editedProperty.management)}
                  managers={editedProperty.managers}
                  onChange={(managers) => setEditedProperty((prev) => ({ ...prev, managers }))}
                />
              </div>

              <div className="button-group">
                <button className="azul-button" onClick={handleUpdate}>Update</button>
                <button className="red-button" onClick={handleCancelEdit}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              <div className="pcl-scroll" style={scrollStyle}>
                <p><strong>Property ID:</strong> {selectedProperty.propertyId || "—"}</p>
                <p><strong>Property Name:</strong> {selectedProperty.propertyName}</p>
                <p><strong>Address:</strong> {selectedProperty.address}</p>
                <p><strong>City:</strong> {selectedProperty.city}</p>
                <p><strong>State:</strong> {selectedProperty.state}</p>
                <p><strong>Zip:</strong> {selectedProperty.zip}</p>
                <p><strong>Client Phone:</strong> {selectedProperty.clientPhone}</p>
                <p><strong>Client Email:</strong> {selectedProperty.clientEmail}</p>
                <p><strong>Accounting Email:</strong> {selectedProperty.accountingEmail}</p>
                <p><strong>Property Type:</strong> {selectedProperty.propertyType}</p>
                <p><strong>Management:</strong> {selectedProperty.management}</p>
                <p><strong>Management Address:</strong> {selectedProperty.managementAddress}</p>
                <p><strong>Management Phone:</strong> {selectedProperty.managementPhone}</p>
                <p><strong>Management Email:</strong> {selectedProperty.managementEmail}</p>
                <p><strong>Management City, State, Zip:</strong> {selectedProperty.managementCityStateZip}</p>
                <div className="pcl-mgr-view">
                  <strong>Managers:</strong>
                  {(() => {
                    const list = readPropertyManagers(selectedProperty, companyByName(selectedProperty.management));
                    if (list.length === 0) return <span className="pcl-muted"> No manager assigned</span>;
                    return list.map((m, i) => (
                      <div className="pcl-mgr-view-row" key={`${m.name}-${i}`}>
                        <span className="pcl-strong">
                          {m.name}{" "}
                          <span className="pcl-chip">{m.title || "Title not set"}</span>
                          {i === 0 && <span className="pcl-mgr-primary">Primary</span>}
                        </span>
                        {(m.email || m.phone) && (
                          <span className="pcl-sub">{[m.email, m.phone].filter(Boolean).join("  |  ")}</span>
                        )}
                      </div>
                    ));
                  })()}
                </div>

                {pricingRows.length > 0 && (
                  <div className="pcl-pricing">
                    <strong>Service Pricing</strong>
                    <span className="pcl-sub">Set from Scheduling, Contracts and Proposals.</span>
                    <table className="pcl-pricing-table">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Initial Fee</th>
                          <th>Recurring Fee</th>
                          <th>Schedule</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricingRows.map((r) => (
                          <tr key={r.code}>
                            <td>{r.code}</td>
                            <td>{formatMoney(r.initialAmount) || <span className="pcl-muted">&mdash;</span>}</td>
                            <td>{formatMoney(r.amount) || <span className="pcl-muted">&mdash;</span>}</td>
                            <td>{r.feeSchedule || <span className="pcl-muted">&mdash;</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="button-group">
                <button className="azul-button" onClick={handleEditClick}>Edit</button>
                <button className="green-button" onClick={() => setShowAddNotePopup(true)}>Add Note</button>
                <button className="red-button" onClick={handleCloseDetails}>Close</button>
              </div>
            </>
          )}
        </Popup>
      )}

      {showDetails && selectedProperty && showAddNotePopup && (
        <Popup level={2} width={520}>
          <h3>Add Note</h3>
          <div className="pcl-scroll" style={scrollStyle}>
            <label>Note:</label>
            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)}></textarea>

            <label>File Upload:</label>
            <input type="file" onChange={handleFileUpload} multiple />

            {uploadedFiles.length > 0 && (
              <div>
                <p><strong>Uploaded Files:</strong></p>
                {uploadedFiles.map((fileURL, index) => (
                  <a key={index} href={fileURL} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
                    View File {index + 1}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="button-group">
            <button className="azul-button" onClick={handleAddNote}>Save Note</button>
            <button className="red-button" onClick={() => setShowAddNotePopup(false)}>Cancel</button>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default PropertiesClients;
